// src/features/ownerAdmin/ServersPage.tsx
import { useState } from 'react';
import {
  useServers, useSetInactivitySettings, useResetGuildActivity, useRemoveBotFromServer,
  useGuildStats, type ServerRow,
} from './useCreatorData';
import { formatGuildName } from '../../lib/formatGuildName';
import { formatRelative } from '../../lib/formatTime';
import './OwnerAdminPage.css';

function OwnerCell({ server }: { server: ServerRow }) {
  if (!server.owner_discord_id) return <span style={{ color: 'var(--text-muted)' }}>Unknown</span>;
  return (
    <div>
      <div>{server.owner_display_name ?? 'Unknown'}</div>
      <div className="owner-admin-table__mono" style={{ fontSize: 11, opacity: 0.7 }}>{server.owner_discord_id}</div>
    </div>
  );
}

function InactivitySettingCell({ server }: { server: ServerRow }) {
  const setInactivity = useSetInactivitySettings();
  const [editing, setEditing] = useState(false);
  const [days, setDays] = useState(String(server.inactivity_leave_days_override ?? 3));

  if (!editing) {
    const display = !server.has_inactivity_override
      ? 'Default (3 days)'
      : server.inactivity_leave_days_override === null
        ? <span style={{ color: 'var(--positive)' }}>Disabled</span>
        : `${server.inactivity_leave_days_override} day(s)`;
    return (
      <div>
        <span>{display}</span>{' '}
        <button className="channel-picker__toggle" onClick={() => setEditing(true)}>Edit</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
      <input className="input" type="number" min={1} max={90} value={days} onChange={(e) => setDays(e.target.value)} style={{ width: 60 }} />
      <button
        className="button-secondary"
        onClick={() => setInactivity.mutate({ guildId: server.guild_id, inactivityLeaveDays: Number(days) }, { onSuccess: () => setEditing(false) })}
        disabled={setInactivity.isPending}
      >
        Save
      </button>
      <button
        className="button-secondary"
        onClick={() => setInactivity.mutate({ guildId: server.guild_id, inactivityLeaveDays: null }, { onSuccess: () => setEditing(false) })}
        disabled={setInactivity.isPending}
      >
        Disable
      </button>
      <button className="button-secondary" onClick={() => setEditing(false)}>Cancel</button>
    </div>
  );
}

function ServerRowActions({ server }: { server: ServerRow }) {
  const reset = useResetGuildActivity();
  const removeBot = useRemoveBotFromServer();
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  function handleRemove() {
    if (!confirmingRemove) { setConfirmingRemove(true); return; }
    removeBot.mutate(server.guild_id);
  }

  return (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <button className="button-secondary" onClick={() => reset.mutate(server.guild_id)} disabled={reset.isPending}>
        Reset timer
      </button>
      <button className="button-danger" onClick={handleRemove} disabled={removeBot.isPending}>
        {confirmingRemove ? 'Click again to confirm' : 'Remove bot'}
      </button>
    </div>
  );
}

function ServersTable({ servers, showActions }: { servers: ServerRow[]; showActions: boolean }) {
  if (servers.length === 0) {
    return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>None.</p>;
  }
  return (
    <table className="owner-admin-table">
      <thead>
        <tr>
          <th>Server</th><th>Owner</th><th>Last active</th><th>Threshold</th>
          {showActions && <th></th>}
        </tr>
      </thead>
      <tbody>
        {servers.map((s, index) => (
          <tr key={s.guild_id} className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
            <td>{formatGuildName(s.guild_name, s.guild_id)}</td>
            <td><OwnerCell server={s} /></td>
            <td className="owner-admin-table__mono">{formatRelative(s.effective_last_seen)}</td>
            <td>{showActions ? <InactivitySettingCell server={s} /> : '—'}</td>
            {showActions && <td><ServerRowActions server={s} /></td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CommandUsageSection() {
  const { data: guilds, isLoading, error } = useGuildStats();

  if (isLoading) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>;
  if (error) return <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load command usage.</p>;
  if (!guilds?.length) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No command activity recorded yet.</p>;

  return (
    <table className="owner-admin-table">
      <thead><tr><th>Server</th><th>Status</th><th>Commands used</th><th>Failures</th></tr></thead>
      <tbody>
        {guilds.map((g, index) => (
          <tr key={g.guild_id} className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
            <td>{formatGuildName(g.guild_name, g.guild_id)}</td>
            <td>
              {g.removed_at
                ? <span className="status-badge status-badge--error">Bot removed</span>
                : <span className="status-badge status-badge--fresh">Active</span>}
            </td>
            <td>{Number(g.total).toLocaleString()}</td>
            <td style={{ color: Number(g.failures) > 0 ? 'var(--negative)' : undefined }}>{g.failures}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ServersPage() {
  const { data: servers, isLoading, error } = useServers();

  const active = (servers ?? []).filter((s) => !s.removed_at);
  const inactive = (servers ?? []).filter((s) => !!s.removed_at);

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Bot owner only</p>
        <h1 className="page-header__title">Servers</h1>
      </div>

      {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
      {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load servers.</p>}

      {servers && (
        <>
          <article className="card" style={{ marginBottom: '1.25rem' }}>
            <header className="card__header">
              <div>
                <p className="card__eyebrow">{active.length} active</p>
                <h2 className="card__title">Active Servers</h2>
              </div>
            </header>
            <div className="card__content"><ServersTable servers={active} showActions /></div>
          </article>

          <article className="card" style={{ marginBottom: '1.25rem' }}>
            <header className="card__header">
              <div>
                <p className="card__eyebrow">{inactive.length} inactive</p>
                <h2 className="card__title">Inactive Servers (Bot Removed)</h2>
              </div>
            </header>
            <div className="card__content"><ServersTable servers={inactive} showActions={false} /></div>
          </article>

          <article className="card">
            <header className="card__header">
              <div>
                <p className="card__eyebrow">Command volume</p>
                <h2 className="card__title">Command Usage by Server</h2>
              </div>
            </header>
            <div className="card__content"><CommandUsageSection /></div>
          </article>
        </>
      )}
    </>
  );
}
